import { Spin } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { ListState, Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { AppConfig, container } from 'src/inversify.config';
import { ClientUsageSummary, ClientUsageSummaryStore } from 'src/stores/clients-store';
import * as Moment from 'moment';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { getDateFormat } from 'src/common/utils';

export const ClientUsage: FC<{ clientId: string }> = ({ clientId }) => {
  const clientUsageSummaryStore = container.get(ClientUsageSummaryStore);
  const clientUsageSummaryState = clientUsageSummaryStore.state;
  const isBusy = clientUsageSummaryState.isBusy;
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'email', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const { t, i18n } = useTranslation();

  const loadUsageData = async () => {
    await clientUsageSummaryStore.loadClientUsage(query, clientId);
  };

  useEffect(() => {
    const initialLoad = async () => {
      await loadUsageData();
    };

    initialLoad();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'Email',
        title: t('Email'),
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={`${clientUsageSummaryStore.baseUrl}/${clientId}/tenants`} fieldName={'email'} fieldValue={'email'} />
        ),
        fiterField: 'Email',
        partialFilter: true,
        renderer: (data) => <span>{data.email}</span>,
      },
      {
        searchable: true,
        sortable: true,
        sortField: 'UserName',
        field: 'userName',
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={`${clientUsageSummaryStore.baseUrl}/${clientId}/tenants`} fieldName={'userName'} fieldValue={'userName'} />
        ),
        title: t('DisplayName'),
        renderer: (data) => <span>{data.userName}</span>,
      },
      {
        searchable: true,
        sortable: true,
        sortField: 'organizationName',
        field: 'organizationName',
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={`${clientUsageSummaryStore.baseUrl}/${clientId}/tenants`}
            fieldName={'organizationName'}
            fieldValue={'organizationName'}
          />
        ),
        title: t('Organization'),
        renderer: (data) => <span>{data.organizationName}</span>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'createdOn',
        field: 'createdOn',
        title: t('CreatedOn'),
        width: '15%',
        renderer: (data) => <span>{Moment.utc(data.createdOn).local().locale(i18n.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
    ],
    sortFields: [],
    data: clientUsageSummaryState.value as ListState<ClientUsageSummary>,
  } as TableModel<ClientUsageSummary>;

  return (
    <div className="client-usage-tab">
      <Spin spinning={isBusy.get()}>
        <TableView
          onRefresh={() => clientUsageSummaryStore.loadClientUsage(query, clientId)}
          rowKey="id"
          canCreateNew={false}
          onQueryChanged={(query: Query) => {
            setQuery(query);
            clientUsageSummaryStore.loadClientUsage(query, clientId);
          }}
          model={tableModel}
          error={
            clientUsageSummaryState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(clientUsageSummaryState.errorMessage.value) : t('ClientUsageTableError'))
          }
        />
      </Spin>
    </div>
  );
};
